import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../Providers/Auth";
import {
  ScrollArea,
  ScrollBar,
} from "../../../../shadcnComponents/ui/scroll-area";
import {
  Bold,
  CaseLower,
  CaseUpper,
  CircleCheck,
  Copy,
  EllipsisVertical,
  Eraser,
  // Image,
  Info,
  Italic,
  Laptop,
  Linkedin,
  Loader,
  Repeat2,
  Send,
  Smartphone,
  Strikethrough,
  Trash,
  Underline,
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../shadcnComponents/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../shadcnComponents/ui/tooltip";
import mixpanel from "mixpanel-browser";
import React from "react";
import { toast } from "../../../../shadcnComponents/ui/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../shadcnComponents/ui/popover";
import { publishOnLinkedIn } from "@/Helpers/helper";

// types ----------------------
export interface InitialEditorProps {
  content?: string;
  dummyText?: string;
  influencerName?: string;
  tagline?: string;
  profileImage?: string;
  images?: File[];
  viewMode?: "desktop" | "mobile";
  docid?: string;
  editorMode?: "edit" | "view";
  status?: 0 | 1 | 2 | 3;
  suggestionComment?: string;
  isApproved?: boolean;
}

// components ----------------------

interface HeaderProps {
  title: string;
  description?: string;
}

const Header = ({ title, description }: HeaderProps) => {
  return (
    <div className="text-center space-y-2">
      <h1 className="text-[#FFF] font-[Inter] text-[20px] not-italic font-bold leading-[24px]">
        {title}
      </h1>
      {description && (
        <p className="text-[#9E9E9E] text-center font-[Inter] text-[14px] not-italic font-normal leading-[20px]">
          {description}
        </p>
      )}
    </div>
  );
};

interface ToolbarProps {
  setViewMode: (mode: string) => void;
  viewMode: string;
  showTrash?: boolean;
  onClickTrash?: () => void;
  showEraser?: boolean;
  onClickEraser?: () => void;
  toolbarOptions?: {
    content: string;
    inputRef: React.RefObject<HTMLTextAreaElement>;
    setContent: (content: string) => void;
  };
}

const unicodeMap = {
  normal: (char: string) => char,
  bold: (char: string) => {
    if (char >= "a" && char <= "z") {
      // Convert lowercase letters to bold
      // return String.fromCodePoint(
      //   char.charCodeAt(0) + 0x1d41a - "a".charCodeAt(0)
      // );
      return String.fromCodePoint(
        char.codePointAt(0) + "𝗮".codePointAt(0) - "a".codePointAt(0)
      );
    } else if (char >= "A" && char <= "Z") {
      // Convert uppercase letters to bold
      // return String.fromCodePoint(
      //   char.charCodeAt(0) + (0x1d400 - "A".charCodeAt(0))
      // );
      return String.fromCodePoint(
        char.codePointAt(0) + "𝗔".codePointAt(0) - "A".codePointAt(0)
      );
    } else if (char >= "0" && char <= "9") {
      // Convert numbers to bold
      // return String.fromCodePoint(
      //   char.charCodeAt(0) + 0x1d7ce - "0".charCodeAt(0)
      // );
      return String.fromCodePoint(
        char.codePointAt(0) + "𝟬".codePointAt(0) - "0".codePointAt(0)
      );
    } else {
      // Leave non-alphabetic characters unchanged
      return char;
    }
  },
  italic: (char: string) => {
    if (char >= "a" && char <= "z") {
      // Convert lowercase letters to bold
      // return String.fromCodePoint(
      //   char.charCodeAt(0) + 0x1d41a - "a".charCodeAt(0)
      // );
      return String.fromCodePoint(
        char.codePointAt(0) + "𝘢".codePointAt(0) - "a".codePointAt(0)
      );
    } else if (char >= "A" && char <= "Z") {
      // Convert uppercase letters to bold
      // return String.fromCodePoint(
      //   char.charCodeAt(0) + (0x1d400 - "A".charCodeAt(0))
      // );
      return String.fromCodePoint(
        char.codePointAt(0) + "𝘈".codePointAt(0) - "A".codePointAt(0)
      );
    } else if (char >= "0" && char <= "9") {
      // Convert numbers to bold
      // return String.fromCodePoint(
      //   char.charCodeAt(0) + 0x1d7ce - "0".charCodeAt(0)
      // );
      return String.fromCodePoint(
        char.codePointAt(0) + "0".codePointAt(0) - "0".codePointAt(0)
      );
    } else {
      // Leave non-alphabetic characters unchanged
      return char;
    }
  },
  // italic: (char: string) => {
  //   if (char >= "A" && char <= "Z") {
  //     return String.fromCodePoint(
  //       char.charCodeAt(0) - "A".charCodeAt(0) + 0x1d434
  //     );
  //   } else if (char >= "a" && char <= "z") {
  //     return String.fromCodePoint(
  //       char.charCodeAt(0) - "a".charCodeAt(0) + 0x1d44e
  //     );
  //   } else if (char >= "0" && char <= "9") {
  //     // Convert numbers to italic
  //     return String.fromCodePoint(
  //       char.charCodeAt(0) + 0x1d7e2 - "0".charCodeAt(0)
  //     );
  //   } else {
  //     return char;
  //   }
  // },
  underline: (char: string) => {
    if (
      (char >= "a" && char <= "z") ||
      (char >= "A" && char <= "Z") ||
      (char >= "0" && char <= "9")
    ) {
      return char + "\u0332";
    } else {
      return char;
    }
  },
  strikethrough: (char: string) => {
    if (
      (char >= "a" && char <= "z") ||
      (char >= "A" && char <= "Z") ||
      (char >= "0" && char <= "9")
    ) {
      return char + "\u0336";
    } else {
      return char;
    }
  },
  uppercase: (char: string) => {
    return char.toUpperCase();
  },
  lowercase: (char: string) => {
    return char.toLowerCase();
  },
};

const Toolbar = ({
  setViewMode,
  viewMode,
  showTrash = false,
  onClickTrash,
  showEraser = false,
  onClickEraser,
  toolbarOptions,
}: ToolbarProps) => {
  const { content, inputRef, setContent } = toolbarOptions;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [history, setHistory] = useState<string[]>([]);

  const formatText = (formatType: keyof typeof unicodeMap) => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start === end) {
      return alert("Please select some text to format.");
    }

    // Save the current content to history
    setHistory((prevHistory) => {
      const newHistory = [...prevHistory, content];
      if (newHistory.length > 10) {
        newHistory.shift(); // Remove the oldest entry if history exceeds 10 items
      }
      return newHistory;
    });

    const selectedText = content?.substring(start, end);
    const formattedText = getFormattedText(selectedText || "", formatType);

    const newText =
      content?.substring(0, start) + formattedText + content?.substring(end);

    setContent(newText);

    textarea.setSelectionRange(
      start + formattedText.length,
      start + formattedText.length
    );
    textarea.focus();
  };

  const getFormattedText = (
    text: string,
    formatType: keyof typeof unicodeMap
  ) => {
    return text
      .split("")
      .map((char) => unicodeMap[formatType](char) || char)
      .join("");
  };

  const copyText = () => {
    //copy content to clipboard
    navigator.clipboard.writeText(content);
    toast({
      title: "Draft content copied to clipboard",
    });
  };

  const handleUndo = useCallback(() => {
    setHistory((prevHistory) => {
      const lastState = prevHistory.pop();
      if (lastState !== undefined) {
        setContent(lastState);
      }
      return [...prevHistory];
    });
  }, [setContent]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && (event.key === "z" || event.key === "z")) {
        event.preventDefault();
        handleUndo();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo]);

  return (
    <div className="relative w-full flex items-center justify-between">
      <div className="flex gap-1 md:gap-2 items-center justify-start min-w-[92px]">
        <button
          className={cn(
            "text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b] aspect-square",
            showEraser ? "block" : "hidden"
          )}
          onClick={onClickEraser}
        >
          <Eraser size={12} color="#b4b3b3" />
        </button>
        <button
          className="text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b] aspect-square"
          onClick={() => formatText("bold")}
        >
          <Bold size={12} color="#b4b3b3" />
        </button>
        <button
          className="text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b] aspect-square"
          onClick={() => formatText("italic")}
        >
          <Italic size={12} color="#b4b3b3" />
        </button>
        <button
          className="hidden md:block text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b] aspect-square"
          onClick={() => formatText("underline")}
        >
          <Underline size={12} color="#b4b3b3" />
        </button>
        <button
          className="hidden md:block text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b] aspect-square"
          onClick={() => formatText("strikethrough")}
        >
          <Strikethrough size={12} color="#b4b3b3" />
        </button>
        <button
          className="text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b] aspect-square"
          onClick={() => copyText()}
        >
          <Copy size={12} color="#b4b3b3" />
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <button className="text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b] aspect-square">
              <EllipsisVertical size={12} color="#b4b3b3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="bg-[#37383A] w-min text-[#b4b3b3] border border-[#424242] rounded-md p-0">
            <div className="p-2 flex flex-col gap-2">
              <button
                className="flex md:hidden flex-row gap-1 md:gap-2 items-center justify-start text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b]"
                onClick={() => formatText("strikethrough")}
              >
                <Strikethrough size={12} color="#b4b3b3" />
                <span className="text-[10px]">Strikethrough</span>
              </button>
              <button
                className="flex md:hidden flex-row gap-1 md:gap-2 items-center justify-start text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b]"
                onClick={() => formatText("underline")}
              >
                <Underline size={12} color="#b4b3b3" />
                <span className="text-[10px]">Underline</span>
              </button>
              <button
                className="flex flex-row gap-1 md:gap-2 items-center justify-start text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b]"
                onClick={() => formatText("uppercase")}
              >
                <CaseUpper size={12} color="#b4b3b3" />
                <span className="text-[10px]">UPPERCASE</span>
              </button>
              <button
                className="flex flex-row gap-1 md:gap-2 items-center justify-start text-[#b4b3b3] text-[12px] border border-[#424242] leading-none px-2 py-2 rounded-md hover:text-[#fff] hover:bg-[#5858588b]"
                onClick={() => formatText("lowercase")}
              >
                <CaseLower size={12} color="#b4b3b3" />
                <span className="text-[10px]">lowercase</span>
              </button>
            </div>
          </PopoverContent>
        </Popover>

        <button
          className={cn(
            "p-2 rounded-md hover:text-[#b4b3b3] hidden",
            "hover:bg-[#5858588b]",
            showTrash && "block"
          )}
          onClick={onClickTrash}
        >
          <Trash size={20} color="#D72427" />
        </button>
      </div>
      <div className="flex gap-2 items-center justify-end md:justify-center flex-grow">
        <span className="text-[#FFF] font-[Inter] text-xs sm:text-[14px] not-italic font-semibold leading-[24px]">
          Preview
        </span>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={19} color="#666666" className="size-3 md:size-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-white border ">
              <p className="w-[300px] text-xs text-center">
                Toggle between mobile and desktop views to see how your content
                would appear on LinkedIn
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="px-2 py-1 bg-[#424242] rounded-md md:flex items-center justify-center hidden">
        {[
          {
            icon: <Smartphone size={20} />,
            mode: "mobile",
          },
          {
            icon: <Laptop size={20} />,
            mode: "desktop",
          },
        ]?.map((item, i) => {
          return (
            <button
              key={i}
              className={cn(
                "p-2 rounded-md hover:text-[#b4b3b3]",
                viewMode === item?.mode && "bg-[#5C5B5B]"
              )}
              onClick={() => setViewMode(item?.mode)}
            >
              {item?.icon}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface InputTextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: React.Ref<HTMLTextAreaElement>;
}

const InputTextArea = React.forwardRef<HTMLTextAreaElement, InputTextAreaProps>(
  ({ ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className="w-full p-5 bg-transparent min-h-[175px] h-full outline-none text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-normal leading-[22px]"
        {...props}
      />
    );
  }
);

interface AddImageAreaProps {
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  onSaveDraft: ({
    e,
    editorState,
  }: {
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>;
    editorState?: InitialEditorProps;
  }) => void;
  onSendForApproval: ({
    e,
    editorState,
  }: {
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>;
    editorState?: InitialEditorProps;
  }) => void;
  editorState: InitialEditorProps;
}

// const AddImageArea = ({
//   handleFileChange,
//   removeImage,
//   onSaveDraft,
//   onSendForApproval,
//   editorState,
// }: AddImageAreaProps) => {
//   return (
//     <div className="w-full p-5">
//       <div className="text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-semibold leading-[22px]">
//         {editorState?.editorMode === "edit" && "Add Images"}
//         {editorState?.editorMode === "view" &&
//           editorState?.images?.length > 0 &&
//           "Image Added"}
//       </div>

//       <ScrollArea className="py-2">
//         <div className="flex gap-2">
//           {editorState?.editorMode === "edit" && (
//             <div className="flex items-center justify-center my-2 mr-2 size-20  shrink-0">
//               <input
//                 id="file-upload"
//                 className="hidden"
//                 type="file"
//                 multiple
//                 accept=".png, .jpg"
//                 onChange={(e) => handleFileChange(e)}
//               />
//               <label
//                 htmlFor="file-upload"
//                 className="flex justify-center items-center border-2 border-dashed border-gray-400 rounded-md cursor-pointer flex-col gap-1 py-2 px-1 text-center shrink-0 size-full"
//               >
//                 <Image size={20} color="#666666" className="shrink-0" />
//                 <span className="text-[#E2E8F0] font-[Inter] text-[10px] not-italic font-normal leading-[22px]">
//                   Select Image
//                 </span>
//               </label>
//             </div>
//           )}

//           {editorState?.images?.map((file, index) => (
//             <div key={index} className="relative m-2 shrink-0">
//               <img
//                 className="w-20 h-20 object-cover rounded-md"
//                 src={file instanceof Blob ? URL.createObjectURL(file) : file}
//                 alt={`preview-${index}`}
//               />
//               {editorState?.editorMode === "edit" && (
//                 <div
//                   onClick={() => removeImage(index)}
//                   className="w-4 h-4 flex items-center justify-center text-sm absolute top-1 right-1 bg-red-500 text-white rounded-full cursor-pointer"
//                 >
//                   &times;
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//         <ScrollBar orientation="horizontal" />
//       </ScrollArea>

//       <div className="flex gap-3 py-2">
//         {editorState?.editorMode === "edit" && (
//           <Button
//             variant="outline"
//             className="bg-transparent"
//             onClick={(e) => {
//               onSaveDraft({ e, editorState });
//             }}
//           >
//             Save Draft
//           </Button>
//         )}
//         <Button
//           variant="destructive"
//           onClick={(e) => onSendForApproval({ e, editorState })}
//           disabled={!(editorState?.editorMode === "edit")}
//         >
//           {editorState?.editorMode === "edit"
//             ? "Send for approval"
//             : "Submitted for approval"}
//         </Button>
//       </div>
//     </div>
//   );
// };

interface PreviewProps {
  content: string;
  viewMode: "mobile" | "desktop";
  dummyText?: string;
  currentUserName?: string;
  profileImage?: string;
  tagline?: string;
  suggestedMode?: boolean;
}

const Preview = ({
  content,
  viewMode,
  dummyText = "",
  currentUserName,
  profileImage,
  tagline,
  suggestedMode,
}: PreviewProps) => {
  const [firstPart, setFirstPart] = useState("");
  const [secondPart, setSecondPart] = useState("");
  const [seeMore, setSeeMore] = useState(false);

  useEffect(() => {
    const splitContent = (text) => {
      const lines = text.split("\n");
      const firstThreeLines = lines.slice(0, 3).join("\n");
      const remainingLines = lines.slice(3).join("\n");
      return [firstThreeLines, remainingLines];
    };
    if (content?.trim()?.length > 0) {
      const trimmedContent = content.trim();
      const [first, second] = splitContent(trimmedContent);
      setFirstPart(first);
      setSecondPart(second);
    } else {
      setFirstPart(dummyText);
      setSecondPart("");
    }
  }, [content, dummyText]);

  return (
    <div
      className={cn(
        "w-full rounded-lg bg-[#1B1F23] flex flex-col p-4 md:p-6 gap-2 transition-all duration-300",
        `${
          viewMode === "mobile"
            ? "min-w-[50px] max-w-[390px]"
            : viewMode === "desktop"
            ? "min-w-[50px] max-w-[555px]"
            : ""
        }`
      )}
    >
      <div className="flex items-center gap-3">
        <img
          src={
            profileImage ||
            "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/RaviAhirwar.png"
          }
          alt="image"
          className="size-[50px] shrink-0 object-cover rounded-full"
        />
        <div className="w-full">
          <div className="text-[#FFF] font-[Inter] text-[14px] not-italic font-semibold leading-[24px] line-clamp-1">
            {currentUserName || "Ravi Ahirwar"}
          </div>
          <p className="text-[#949698] font-[Inter] text-[12px] not-italic font-normal leading-[normal] line-clamp-1 w-2/3">
            {tagline || "Founder, anchors | IIT Bombay"}
          </p>
          <p className="text-[#949698] font-[Inter] text-[12px] not-italic font-normal leading-[normal] line-clamp-1 w-2/3 mt-1">
            1d
          </p>
        </div>
      </div>
      <div className="pb-3 relative">
        {suggestedMode ? (
          <p>
            <span
              className={cn(
                "text-[#C7C3C3] font-inter text-[12px] not-italic font-normal leading-[22px] relative overflow-hidden",
                seeMore ? "line-clamp-none" : "line-clamp-3"
              )}
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
              }}
            >
              {content || dummyText}
            </span>
            <div
              className={cn(
                "text-[#64748B] font-[Inter] text-[12px] not-italic font-normal leading-[22px] hover:underline bg-[#1b1f23] cursor-pointer w-fit text-right bottom-3 right-0",
                seeMore ? "block" : " absolute"
              )}
              onClick={() => setSeeMore(!seeMore)}
            >
              {seeMore ? "...see less" : "...see more"}
            </div>
          </p>
        ) : (
          <>
            <p>
              <span
                className="text-[#C7C3C3] font-inter text-[12px] not-italic font-normal leading-[22px] relative overflow-hidden"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  textOverflow: "clip",
                  whiteSpace: "pre-wrap",
                }}
              >
                {firstPart}
                <span className="text-[#3771C8] font-[Inter] text-[12px] not-italic font-normal leading-[22px] hover:underline absolute right-0 bottom-0 bg-[#1b1f23]">
                  ...see more
                </span>
              </span>
            </p>
            <p
              className="text-[#666] font-Inter text-[12px] not-italic font-normal leading-[22px]"
              style={{
                whiteSpace: "pre-wrap",
              }}
            >
              {secondPart}
            </p>
          </>
        )}
      </div>
      <div className="flex gap-2">
        <img
          src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727856335032-Frame_1430102479_%281%29.png"
          alt="reactions"
          className="h-4"
        />
        <span className="text-[#949698] font-[Inter] text-[12px] not-italic font-bold leading-[normal]">
          Aesha Agarwal and 76 others
        </span>
      </div>
      <div className="flex gap-5 items-center pt-3">
        <img
          src={
            profileImage ||
            "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/RaviAhirwar.png"
          }
          alt="image"
          className="size-[30px] shrink-0 object-cover rounded-full"
        />
        {[
          {
            icon: (
              <img
                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727853788915-ei_like.png"
                alt="like"
                className="w-[30px] h-[30px]"
              />
            ),
            title: "Like",
          },
          {
            icon: (
              <img
                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727855749998-lets-icons_comment-light.png"
                alt="comment"
                className="w-[30px] h-[30px]"
              />
            ),
            title: "Comment",
          },
          {
            icon: <Repeat2 size={24} color="#787b7d" />,
            title: "Repost",
          },
          {
            icon: <Send size={19} color="#787b7d" />,
            title: "Share",
          },
        ]?.map((item, i) => {
          return (
            <div key={i} className="flex gap-1 items-center justify-center">
              {item?.icon}
              {!(viewMode === "mobile") && (
                <span className="text-[#949698] font-inter text-[12px] items-center justify-center hidden md:flex">
                  {item?.title}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SuggestionArea = ({ editorState }) => {
  const [seeMore, setSeeMore] = useState(false);
  return (
    <div className="p-5">
      {editorState?.images?.length > 0 && (
        <div className="w-full">
          <div className="text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-semibold leading-[24px]">
            Image(s) to be added
          </div>

          <ScrollArea className="py-2">
            <div className="flex gap-2">
              {editorState?.images?.map((file, index) => (
                <div key={index} className="relative m-2 shrink-0">
                  <img
                    className="w-20 h-20 object-cover rounded-md"
                    src={
                      file instanceof Blob ? URL.createObjectURL(file) : file
                    }
                    alt={`preview-${index}`}
                  />
                </div>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      )}
      {editorState?.suggestionComment?.length > 0 && (
        <>
          <div className="text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-semibold leading-[24px] pb-2">
            Suggestion
          </div>
          <div className="bg-[#1B1F23] p-3 rounded-lg w-fit">
            <p
              className={cn(
                "text-[#949698] font-[Inter] text-[12px] not-italic font-normal leading-[22px] whitespace-pre-wrap cursor-pointer max-w-sm break-words",
                seeMore ? "line-clamp-none" : "line-clamp-6"
              )}
              onClick={() => setSeeMore(!seeMore)}
            >
              {editorState?.suggestionComment}
            </p>
          </div>
        </>
      )}
      {editorState?.isApproved && (
        <div
          className={
            "py-1 px-4 font-[Inter] text-[12px] not-italic font-semibold leading-[24px] rounded-[6px] uppercase bg-[#36D7CC33]/20 text-[#fff] flex gap-2 items-center w-fit"
          }
        >
          <CircleCheck fill="#115E59" color="#fff" />
          APPROVED DRAFT FOR CAMPAIGN
        </div>
      )}
    </div>
  );
};

// LinkedinPostEditor ----------------------

interface LinkedinPostEditorProps {
  initial?: InitialEditorProps;
  header?: HeaderProps;
  onSaveDraft?: AddImageAreaProps["onSaveDraft"];
  onSendForApproval?: AddImageAreaProps["onSendForApproval"];
  onChange?: (editorState: InitialEditorProps) => void;
  showTrash?: boolean;
  clearEditor?: () => void;
}

export const LinkedinPostEditor = ({
  initial = {},
  header,
  onSaveDraft,
  showTrash = false,
  clearEditor,
}: LinkedinPostEditorProps) => {
  const authState = useAuth();
  const [editorState, setEditorState] = useState<InitialEditorProps>({
    content: "",
    dummyText: (
      <div>
        <p>
          <b>
            Make sure your LinkedIn post hits the mark before it goes live! 📝
          </b>
        </p>{" "}
        <br />
        <p>
          With our <b>LinkedIn Post Preview Tool</b>, you can:
        </p>
        ✅ <b>Preview your post</b> to keep your key message above the ‘see
        more’ cut-off. <br />✅ <b>Share a preview link</b> with your team for
        feedback. <br />✅ <b>Make edits easily</b>, with every version getting
        its own shareable link <br /> <br />
        No guesswork, just clean, clear content. <br />
        <b>👉 Give it a try and see the difference!</b> <br />
        Let me know if this feels better! 😊
      </div>
    ),
    influencerName: "",
    tagline: "",
    profileImage: "",
    images: [],
    viewMode: "desktop",
    editorMode: "edit",
  });
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(
    null
  ) as React.RefObject<HTMLTextAreaElement>;

  useEffect(() => {
    setEditorState((prev) => ({
      ...prev,
      influencerName:
        initial?.influencerName || authState?.loggedUser?.name || "",
      tagline: initial?.tagline || authState?.loggedUser?.tagline || "",
      profileImage:
        initial?.profileImage || authState?.loggedUser?.profile || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    setEditorState((prev) => ({
      ...prev,
      ...initial,
    }));
  }, [initial]);
  const handleViewModeChange = (mode: InitialEditorProps["viewMode"]) => {
    setEditorState((prev) => ({
      ...prev,
      viewMode: mode,
    }));
  };

  const handleEditorTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setEditorState((prev) => ({ ...prev, content: e.target.value }));
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files) {
  //     setEditorState((prev) => ({
  //       ...prev,
  //       images: [...Array.from(e.target.files), ...prev.images],
  //     }));
  //   }
  // };

  // const handleImageRemove = (index: number) => {
  //   setEditorState((prev) => ({
  //     ...prev,
  //     images: prev.images.filter((_, i) => i !== index),
  //   }));
  // };

  // this effect is causing infinite re-rendering
  // useEffect(() => {
  //   onChange(editorState);
  // }, [editorState]);

  return (
    <>
      <div className="flex flex-col gap-5">
        {header && (
          <Header title={header?.title} description={header?.description} />
        )}
        <div className="flex flex-col">
          <div className="border border-[#37383A] px-2 md:px-7 py-2 rounded-t-lg bg-[#37383A]">
            <Toolbar
              setViewMode={handleViewModeChange}
              viewMode={editorState?.viewMode}
              // onClickTrash={clearEditor}
              // showTrash={showTrash}
              showEraser={editorState?.content?.length > 0}
              onClickEraser={() =>
                setEditorState((prev) => ({ ...prev, content: "" }))
              }
              toolbarOptions={{
                content: editorState?.content,
                inputRef: inputRef,
                setContent: (updatedContent) => {
                  setEditorState((prev) => ({
                    ...prev,
                    content: updatedContent,
                  }));
                },
              }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 min-h-[400px]">
            <div
              className="col-span-5 flex flex-col border border-[#37383A] rounded-none md:rounded-l-lg md:rounded-t-none"
              style={{
                background:
                  "radial-gradient(107.32% 141.42% at 0% 0%, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.00) 100%), #37383A",
              }}
            >
              {editorState?.isApproved || editorState?.suggestionComment ? (
                <SuggestionArea editorState={editorState} />
              ) : (
                <>
                  <div className="flex-1">
                    <InputTextArea
                      ref={inputRef}
                      placeholder="Type your text...."
                      onChange={handleEditorTextChange}
                      value={editorState?.content}
                      style={{ scrollbarWidth: "thin" }}
                      disabled={!(editorState?.editorMode === "edit")}
                    />
                  </div>
                  {/* <AddImageArea
                    handleFileChange={handleImageChange}
                    removeImage={handleImageRemove}
                    onSaveDraft={onSaveDraft}
                    onSendForApproval={onSendForApproval}
                    editorState={editorState}
                  /> */}
                  <div className="w-full p-2 md:p-5 flex justify-between gap-2">
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        onClick={async (e) => {
                          mixpanel.track(
                            "Linkedin Post Preview: Save and generate Link: clicked"
                          );
                          setIsSaving(true);
                          await onSaveDraft({ e, editorState });
                          setIsSaving(false);
                        }}
                        disabled={!(editorState?.editorMode === "edit")}
                        className="w-full p-2 md:p-auto md:w-fit"
                      >
                        {!isSaving ? (
                          "Save and generate link"
                        ) : (
                          <Loader className="animate-spin " />
                        )}
                      </Button>
                      {/* share on linkedin button */}
                      <Button
                        variant="secondary"
                        disabled={!editorState?.content}
                        onClick={() => {
                          mixpanel.track(
                            "Linkedin Post Preview: Publish on LinkedIn: clicked"
                          );
                          publishOnLinkedIn(editorState?.content);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                      >
                        <Linkedin className="h-4 w-4 mr-2" />
                        Publish on LinkedIn
                      </Button>
                    </div>

                    <button
                      className={cn(
                        "p-2 rounded-md hover:text-[#b4b3b3] hidden",
                        "hover:bg-[#5858588b]",
                        showTrash && "block"
                      )}
                      onClick={clearEditor}
                    >
                      <Trash size={20} color="#D72427" />
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="col-span-7 flex flex-col rounded-b-lg md:rounded-r-lg md:rounded-bl-none md:rounded-tr-none bg-[#37383A] items-center">
              <ScrollArea className="p-2 md:p-6 transition-transform">
                <Preview
                  content={editorState?.content}
                  viewMode={editorState?.viewMode}
                  dummyText={editorState?.dummyText}
                  currentUserName={editorState?.influencerName}
                  profileImage={editorState?.profileImage}
                  tagline={editorState?.tagline}
                  suggestedMode={
                    // !!editorState?.suggestionComment || editorState?.isApproved
                    true
                  }
                />
              </ScrollArea>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
